import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAttendanceStore } from '../stores/attendanceStore';
import { getTableStatus, getTotalAvailableSeats } from '../utils/tableUtils';
import Card from '../components/common/Card';
import Button from '../components/common/Button';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    students,
    attendanceRecords,
    currentDate,
    resetTables,
    removeAttendance,
    toggleParkingRegistration,
    updateTableCapacity,
    saveTableSettings,
    loadTableSettings,
  } = useAttendanceStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [editingTable, setEditingTable] = useState<number | null>(null);
  const [editingCapacity, setEditingCapacity] = useState<number>(0);
  const tables = getTableStatus();

  // 오늘 출석한 학생들 필터링
  const todayAttendance = useMemo(() => {
    return attendanceRecords.filter(record => record.date === currentDate);
  }, [attendanceRecords, currentDate]);

  // 검색 필터링
  const filteredAttendance = useMemo(() => {
    if (!searchTerm) return todayAttendance;
    
    return todayAttendance.filter(record =>
      record.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (record.parkingNumber && record.parkingNumber.includes(searchTerm))
    );
  }, [todayAttendance, searchTerm]);

  // 페이지네이션
  const totalPages = Math.ceil(filteredAttendance.length / itemsPerPage);
  const paginatedAttendance = filteredAttendance.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleResetTables = () => {
    if (window.confirm('모든 테이블을 초기화하시겠습니까?')) {
      resetTables();
      alert('테이블이 초기화되었습니다.');
    }
  };

  const handleRemoveAttendance = (studentId: string) => {
    if (window.confirm('이 학생의 출석을 취소하시겠습니까?')) {
      removeAttendance(studentId);
    }
  };

  const handleExportData = () => {
    const data = {
      date: currentDate,
      totalStudents: students.length,
      attendanceCount: todayAttendance.length,
      attendanceRate: ((todayAttendance.length / students.length) * 100).toFixed(1),
      tables: tables,
      attendance: todayAttendance,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${currentDate}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleEditTable = (tableNumber: number, currentCapacity: number) => {
    setEditingTable(tableNumber);
    setEditingCapacity(currentCapacity);
  };

  const handleSaveTableCapacity = () => {
    if (editingTable && editingCapacity > 0) {
      updateTableCapacity(editingTable, editingCapacity);
      setEditingTable(null);
      setEditingCapacity(0);
    }
  };

  const handleCancelEdit = () => {
    setEditingTable(null);
    setEditingCapacity(0);
  };

  const handleLoadSettings = () => {
    loadTableSettings();
  };

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 헤더 */}
          <Card className="mb-6">
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">🔧 관리자 페이지</h1>
              <p className="text-gray-600">출석 및 테이블 현황 관리</p>
            </div>
          </Card>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-800">{students.length}</div>
                <div className="text-gray-600">전체 학생</div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-success">{todayAttendance.length}</div>
                <div className="text-gray-600">오늘 출석</div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">
                  {((todayAttendance.length / students.length) * 100).toFixed(1)}%
                </div>
                <div className="text-gray-600">출석률</div>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="text-2xl font-bold text-secondary">{getTotalAvailableSeats()}</div>
                <div className="text-gray-600">잔여석</div>
              </div>
            </Card>
          </div>

          {/* 테이블 현황 */}
          <Card className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">📊 테이블 현황</h2>
              <Button
                onClick={handleLoadSettings}
                variant="secondary"
                size="sm"
                icon="🔄"
              >
                설정 불러오기
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tables.map((table) => (
                <div
                  key={table.tableNumber}
                  className="bg-white/10 rounded-lg p-4 text-center relative"
                >
                  <div className="text-2xl font-bold text-gray-800 mb-2">
                    {table.tableNumber}번 테이블
                  </div>
                  
                  {editingTable === table.tableNumber ? (
                    <div className="space-y-2">
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={editingCapacity}
                        onChange={(e) => setEditingCapacity(Number(e.target.value))}
                        className="w-16 text-center bg-white/20 rounded px-2 py-1 text-gray-800"
                      />
                      <div className="flex justify-center space-x-1">
                        <button
                          onClick={handleSaveTableCapacity}
                          className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                        >
                          저장
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-gray-600 mb-2">{table.type}</div>
                      <div className="text-lg font-semibold text-accent">
                        {table.currentOccupancy}/{table.capacity}
                      </div>
                      <div className="text-sm text-gray-500">
                        {((table.currentOccupancy / table.capacity) * 100).toFixed(0)}% 점유
                      </div>
                      <button
                        onClick={() => handleEditTable(table.tableNumber, table.capacity)}
                        className="absolute top-2 right-2 text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                      >
                        편집
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* 학생 관리 */}
          <Card className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">📊 학생 관리</h2>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="이름 또는 차량번호로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field w-64"
                />
              </div>
            </div>

            {/* 테이블 헤더 */}
            <div className="grid grid-cols-6 gap-4 p-3 bg-white/10 rounded-lg mb-2 font-semibold text-gray-800">
              <div>이름</div>
              <div>주차번호</div>
              <div>날짜</div>
              <div>테이블</div>
              <div>등록</div>
              <div>액션</div>
            </div>

            {/* 테이블 데이터 */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {paginatedAttendance.map((record) => (
                <motion.div
                  key={record.studentId}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="grid grid-cols-6 gap-4 p-3 bg-white/5 rounded-lg text-gray-800"
                >
                  <div className="font-medium">{record.studentName}</div>
                  <div>{record.parkingNumber || '-'}</div>
                  <div>{record.date}</div>
                  <div>{record.assignedTable ? `${record.assignedTable}번` : '-'}</div>
                  <div>
                    <input
                      type="checkbox"
                      checked={record.isParkingRegistered}
                      onChange={() => toggleParkingRegistration(record.studentId)}
                      className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                    />
                  </div>
                  <div>
                    <button
                      onClick={() => handleRemoveAttendance(record.studentId)}
                      className="text-red-500 hover:text-red-600 text-sm"
                    >
                      취소
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="flex justify-center space-x-2 mt-4">
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                >
                  이전
                </Button>
                <span className="text-gray-700 self-center">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === totalPages}
                >
                  다음
                </Button>
              </div>
            )}
          </Card>

          {/* 액션 버튼들 */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <button
              onClick={handleResetTables}
              className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              🔄 테이블 초기화
            </button>
            
            <button
              onClick={saveTableSettings}
              className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              💾 설정 저장
            </button>
            
            <button
              onClick={handleExportData}
              className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              📤 데이터 내보내기
            </button>
            
            <button
              onClick={() => navigate('/attendance')}
              className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              📚 출석 페이지
            </button>
            
            <button
              onClick={handleGoHome}
              className="w-full px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
            >
              🏠 홈으로
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminPage; 